import _ from "lodash";
import { Form } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import * as yup from "yup";

import {
  adultDobValidationSchemaFactory,
  noInsuranceCarrierValidationSchemaFactory,
} from "@/utils/yupSchemaUtils";
import { Button2 } from "@/ui2/atoms/Button";
import { carrierMemberIdValidationSchema } from "@/components/Common/InsuranceEligibility/carrierMetadata";
import { CATCH_ALL_INSURANCE_OPTIONS } from "@/core/constants";
import {
  createInsurancePolicyAcknowledgement,
  trackInsurancePolicyAcknowledgement,
} from "@/utils/acknowledgementUtils";
import { CustomForm } from "@/component-library/CustomForm";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import images from "@/utils/images";
import InsuranceEligibilityWrapper from "@/components/Common/InsuranceEligibility/InsuranceEligibilityWrapper";
import { patientRequests } from "@/api/(requests)/patient";
import { SelfSignupInsuranceHOC } from "@/component-library/hocs/SelfSignUpInsuranceHOC";
import { testLocator } from "@/lib/helpers/test-locator";
import { trackEvent } from "@/utils/EventTrackingUtil";

import { INITIAL_INSURANCE_VALUES } from "../SelfSignup";

import "../style.scss";

export function AddInsurance(props) {
  const { accountData, nextStep, setAccountData, setInsuranceEligibilityResponse } = props;
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [interestedInGuardianship, setInterestedInGuardianship] = useState(false);
  const [submissionAttempts, setSubmissionAttempts] = useState(0);
  const [showAcknowledgementPopup, setShowAcknowledgementPopup] = useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState(null);
  const [requiredFields, setRequiredFields] = useState(null);
  const [skipSelected, setSkipSelected] = useState(false);

  useEffect(() => {
    setInterestedInGuardianship(accountData.interested_in_guardianship);
    try {
      (async () => {
        const rawInsuranceList = await patientRequests.fetch_insurance_list();

        if (rawInsuranceList && rawInsuranceList.carriers) {
          setInsuranceOptions(
            rawInsuranceList.carriers.map((carrier) => {
              return { label: carrier, value: carrier };
            }),
          );
        }
      })();
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    if (skipSelected) {
      nextStep(false);
    }
  }, [skipSelected]);

  const supplementInsuranceInfo = (data) => {
    const policyHolderQuestion = _.get(data, "primary_insurance.policy_holder_question");
    const carrier = _.get(data, "primary_insurance.carrier");

    if (!policyHolderQuestion && !CATCH_ALL_INSURANCE_OPTIONS.includes(carrier)) {
      data["primary_insurance"]["policy_holder_question"] = "yes";
      data["primary_insurance"]["policy_holder_name"] = `${data.first_name} ${data.last_name}`;
      data["primary_insurance"]["policy_relationship"] = "self";
    }

    return data;
  };

  const isAcknowledgementRequired = (carrier) => {
    return CATCH_ALL_INSURANCE_OPTIONS.includes(carrier);
  };

  useEffect(() => {
    if (accountData && !_.isEmpty(accountData.acknowledgements)) {
      setShowAcknowledgementPopup(false);
      nextStep();
    }
  }, [accountData]);

  const submitAcknowledgement = async (formData) => {
    const mergedAccountData = await transformAccountData(formData);
    const acknowledgement = await createInsurancePolicyAcknowledgement(
      formData.primary_insurance.carrier,
    );
    const mergedAcknowledgements = {
      ...mergedAccountData.acknowledgements,
      ...acknowledgement,
    };

    setAccountData({ ...mergedAccountData, acknowledgements: mergedAcknowledgements });
  };

  const transformAccountData = async (formData) => {
    return {
      ...accountData,
      ...formData,
    };
  };

  const transformEligibilityDetails = (formData, mergedAccountData) => {
    const primaryInsurance = _.cloneDeep(accountData.primary_insurance);

    delete primaryInsurance["card_front_preview"];
    delete primaryInsurance["card_back_preview"];
    const finalAccountData = supplementInsuranceInfo(mergedAccountData);
    const insuranceInfo = {
      ...primaryInsurance,
      area: "patient_self_signup",
      carrier: formData.primary_insurance.carrier,
      dob: moment(finalAccountData.dob, "MMDDYYYY").format("MM/DD/YYYY"),
      email: finalAccountData.email,
      first_name: finalAccountData.first_name,
      group_id: formData?.primary_insurance?.group_id,
      last_name: finalAccountData.last_name,
      member_id: formData.primary_insurance.member_id,
      policy_dob: moment(finalAccountData.dob, "MMDDYYYY").format("MM/DD/YYYY"),
    };

    setInsuranceDetails(insuranceInfo);
    setAccountData(finalAccountData);
  };

  const submitOrOpenAcknowledgement = async (formData) => {
    if (isAcknowledgementRequired(formData.primary_insurance.carrier)) {
      setShowAcknowledgementPopup(true);

      return;
    }

    setShowAcknowledgementPopup(false);
    const mergedAccountData = await transformAccountData(formData);

    transformEligibilityDetails(formData, mergedAccountData);
    setSubmissionAttempts(submissionAttempts + 1);
  };

  const skipInsurance = () => {
    setAccountData({
      ...accountData,
      primary_insurance: _.cloneDeep(INITIAL_INSURANCE_VALUES),
    });
    setSkipSelected(true);
  };

  const eligibilityCallback = (eligibilityMessage = "", submissionAttempts = 0, response) => {
    setInsuranceEligibilityResponse(response);
    const isCarrierSelected = !!_.get(insuranceDetails, "carrier", false);

    if (isCarrierSelected || (interestedInGuardianship && !insuranceDetails)) {
      if (submissionAttempts < 5 && eligibilityMessage === "") {
        trackInsurancePolicyAcknowledgement(insuranceDetails.carrier);
        trackEvent("Self Sign-up Insurance Information V2");
      }
      nextStep(isCarrierSelected || (interestedInGuardianship && !insuranceDetails));
    }
  };

  const insuranceCarrierValidation = () => {
    let validation = yup.object().shape({
      primary_insurance: yup.object().shape({
        carrier: yup.string().required("Carrier is required").nullable(),
        group_id: yup
          .string()
          .matches(/^[a-zA-Z0-9]*$/, "Enter a valid Group ID")
          .nullable(),
        member_id: noInsuranceCarrierValidationSchemaFactory(
          "carrier",
          carrierMemberIdValidationSchema,
        ),
      }),
    });

    if (!requiredFields) {
      return validation;
    }

    if (requiredFields.includes("dob")) {
      validation = validation.concat(
        yup.object().shape({
          dob: adultDobValidationSchemaFactory(
            "MMDDYYYY",
            "Must be a valid date",
            `You must be 18 or older to register yourself for services.
                        Children aged 5-17 must have a parent register first.`,
          ).required("Date of birth is required"),
        }),
      );
    }

    if (requiredFields.includes("first_name")) {
      validation = validation.concat(
        yup.object().shape({
          first_name: yup
            .string()
            .required("First name is required")
            .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
        }),
      );
    }

    if (requiredFields.includes("last_name")) {
      validation = validation.concat(
        yup.object().shape({
          last_name: yup
            .string()
            .required("Last name is required")
            .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
        }),
      );
    }

    return validation;
  };

  const renderForm = (formikProps) => {
    return (
      <Form>
        <div className="mx-auto px-0 mb-3">
          <div className="container px-0">
            <SelfSignupInsuranceHOC
              className="AddInsurance--self-signup"
              closeAcknowledgementHandler={() => setShowAcknowledgementPopup(false)}
              formikProps={formikProps}
              hideFileUpload={true}
              insuranceCompanyOptions={insuranceOptions}
              isPrimaryInsurance={true}
              requiredFields={requiredFields}
              showAcknowledgementPopup={showAcknowledgementPopup}
              submitAcknowledgementHandler={() => submitAcknowledgement(formikProps.values)}
            />
          </div>
        </div>

        <div className="tw:mt-10 tw:flex tw:justify-center tw:gap-6">
          {interestedInGuardianship && (
            <Button2
              className="tw:min-w-40"
              onPress={skipInsurance}
              size="xl"
              variant="secondary"
              {...getAutomationDataAttr("insurance-skip-button")}
              {...testLocator("insuranceSkipButton")}
            >
              Skip
            </Button2>
          )}

          <Button2
            className="tw:min-w-40"
            isDisabled={submissionAttempts > 4}
            size="xl"
            type="submit"
            {...getAutomationDataAttr("insurance-submit-button")}
            {...testLocator("insuranceContinueButton")}
          >
            Continue
          </Button2>
        </div>
      </Form>
    );
  };

  return (
    <div className="p-5">
      <div className="fs-22 txt-pri mb-5 text-center tw:font-bold">
        Almost there! Enter your insurance.
      </div>

      <div className="text-center my-5">
        <img
          alt="Step 3/3"
          className="signup-progress-bar"
          src={images("./signup/progress-3.png")}
        />
      </div>

      {insuranceDetails && (
        <InsuranceEligibilityWrapper
          eligibilityCallback={eligibilityCallback}
          entryPoint="self_signup"
          insuranceDetails={insuranceDetails}
          setRequiredFields={setRequiredFields}
          submissionAttempts={submissionAttempts}
        />
      )}

      <CustomForm
        initialValues={accountData}
        onSubmit={submitOrOpenAcknowledgement}
        render={renderForm}
        validationSchema={insuranceCarrierValidation()}
      />
    </div>
  );
}
